import React, { useState, useEffect, useRef } from 'react';
import pin from '../../Pages/Worldmap/SVG/pin.svg'
import shoppingIcon from '../../Pages/Worldmap/SVG/2_SHOPPING.png';
import hotelIcon from '../../Pages/Worldmap/SVG/3_SONETTE.png';
import officeIcon from '../../Pages/Worldmap/SVG/8_OFFICE_STROKE.png';
import restaurantIcon from '../../Pages/Worldmap/SVG/5_CUISINE.png';
import culturalIcon from '../../Pages/Worldmap/SVG/6_PEINTURE.png';
import residenceIcon from '../../Pages/Worldmap/SVG/7_SERRURE.png';
import urbanIcon from '../../Pages/Worldmap/SVG/8_BANC.png';
import retailIcon from '../../Pages/Worldmap/SVG/1_CHARIOT.png';
import installationIcon from '../../Pages/Worldmap/SVG/9_INSTALLATION_SCENOGRAPHY.png';
import FD1 from '../../Pages/Worldmap/SVG/9_ROUGE.png';
import FD2 from '../../Pages/Worldmap/SVG/10_DEGRADE.png';
import FD3 from '../../Pages/Worldmap/SVG/11_POINTILLE2024.png';
import city from '../../Pages/Worldmap/SVG/city.png';
import architectureIcon from '../../Pages/Worldmap/SVG/4_OFFICE.png';
import ouiDesignIcon from '../../Pages/Worldmap/SVG/oui_design.png';
import parisDesignWeekIcon from '../../Pages/Worldmap/SVG/paris_design_week.png';
import link from '../../Utils/Format';
import AddressComponent from './AdressComponent';

// Helper function to format category names for display
const formatCategoryForDisplay = (category) => {
    if (category === "Installation/Scenography") {
        return "Installation / Scenography";
    }
    return category;
};

const POINT_ICONS = {
  "Retail": shoppingIcon,
  "Hotel": hotelIcon,
  "Office": officeIcon,
  "Restaurant/bar": restaurantIcon,
  "Cultural space": culturalIcon,
  "Private residence": residenceIcon,
  "Urban space/Transportation": urbanIcon,
  "Architecture": architectureIcon,
  "Installation/Scenography": installationIcon
};

const EVENT_TYPE_ICONS = {
  "Exhibition": culturalIcon,
  "Pop-up exhibition": culturalIcon,
  "Panel discussion": culturalIcon,
  "Talk": culturalIcon,
  "Open House": residenceIcon,
  "Gallery Tour": culturalIcon,
  "Opening Reception": culturalIcon,
  "Cocktail Reception": retailIcon,
  "Breakfast": restaurantIcon,
  "Special Event": culturalIcon,
  "Presentation": culturalIcon,
  "Gallery Walkthrough": culturalIcon,
  "Private Visit": residenceIcon,
  "Open Showroom": retailIcon,
  "Guided Tour": urbanIcon
};

const AWARDS_ICONS = {
  "Le French Design 100 - 2019": FD1,
  "Le French Design 100 - 2022": FD2,
  "Le French Design 100 - 2024": FD3
};

const DESIGN_EVENT_ICONS = {
  "In the City": city,
  "Oui Design! 2025": ouiDesignIcon,
  "Paris Design Week 2025": parisDesignWeekIcon
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Check if it's already in a nice format (e.g., "May 15, 2025")
    if (dateString.includes(',')) return dateString;
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

const PropertiesContainer = ({ properties }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  
  // Check if we're on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Check if description is overflowing
  useEffect(() => {
    const checkOverflow = () => {
      if (descriptionRef.current) {
        const element = descriptionRef.current;
        setIsOverflowing(element.scrollHeight > element.clientHeight);
      }
    };
    
    // Run after a small delay to ensure content is fully rendered
    const timer = setTimeout(checkOverflow, 100);
    
    // Also check when window is resized
    window.addEventListener('resize', checkOverflow);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [properties.description_projet, descriptionExpanded]);
  
  const sectionStyle = {
    borderBottom: '1px solid black',
    padding: isMobile ? '8px' : '10px',
    marginBottom: isMobile ? '8px' : '10px'
  };
  
  const multiLineEllipsisStyle = {
    display: descriptionExpanded ? 'block' : '-webkit-box',
    WebkitLineClamp: '5',
    WebkitBoxOrient: 'vertical',
    overflow: descriptionExpanded ? 'auto' : 'hidden',
    textOverflow: 'ellipsis',
    transition: 'max-height 0.3s ease'
  };

  const renderCategories = () => {
    // Determine if this is an Oui Design or Paris Design Week event
    const isOuiDesign = properties.design_events && 
                        (properties.design_events === 'Oui Design! 2025' || 
                         properties.design_events === 'Oui Design 2025');
    
    const isParisDesignWeek = properties.design_events && 
                              properties.design_events === 'Paris Design Week 2025';
    
    return (
      <div style={{ ...sectionStyle, display: 'flex', justifyContent: 'space-between' }}>
        {/* Show only event type text for Oui Design and Paris Design Week events (no icon) */}
        {(isOuiDesign || isParisDesignWeek) && properties.event_type && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'left', gap: '8px' }}>
            <p style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              textAlign: 'left', 
              color: 'black', 
              margin: 0, 
              textTransform: 'uppercase'
            }}>
              {properties.event_type}
            </p>
          </div>
        )}
        
        {/* Show points of interest for regular projects */}
        {!isOuiDesign && !isParisDesignWeek && properties.points_of_interest && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'left', gap: '8px' }}>
            <img 
              src={POINT_ICONS[properties.points_of_interest]} 
              alt={properties.points_of_interest}
              style={{
                width: isMobile ? '20px' : '24px',
                height: isMobile ? '20px' : '24px',
                objectFit: 'contain'
              }}
            />
            <p style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              textAlign: 'left', 
              color: 'black', 
              margin: 0, 
              textTransform: 'uppercase'
            }}>
              {formatCategoryForDisplay(properties.points_of_interest)}
            </p>
          </div>
        )}
        
        {/* Display event badge */}
        {properties.design_events && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'right', gap: '8px' }}>
            <img 
              src={DESIGN_EVENT_ICONS[properties.design_events] || city} 
              alt={properties.design_events}
              style={{
                width: isMobile ? '30px' : '36px',
                height: isMobile ? '30px' : '36px',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const getMainInfoLink = () => {
    if (properties.projectType === 'itc' || properties.projectType === 'oui' || properties.projectType === 'pdw') {
      return { href: link(properties.site_web), target: '_blank', rel: 'noopener noreferrer' };
    }
    if (properties.projectType === 'pos') {
      if (properties.website) {
        return { href: link(properties.website), target: '_blank', rel: 'noopener noreferrer' };
      }
      return null;
    }
    return { href: `https://connectedby.lefrenchdesign.org/company?id=${properties.mongo_company_id}`, target: '_self', rel: '' };
  };

  const renderMainInfo = () => {
    const linkInfo = getMainInfoLink();
    const nameContent = (
      <strong style={{
        color: '#0000ff',
        fontSize: isMobile ? '16px' : '18px',
        display: 'block',
        fontWeight: '500',
        textTransform: 'uppercase',
        cursor: linkInfo ? 'pointer' : 'default'
      }}>
        {linkInfo && <img
          alt=""
          style={{
            margin: "0 5px 0 0",
            display: "inline-block",
            height: isMobile ? "12px" : "14px"
          }}
          src="/img/fleche_bleue.png"
        />}
        {properties.fiche_agence || properties.nom_etablissement}
      </strong>
    );

    return (
    <div style={sectionStyle}>
      {linkInfo ? (
        <a href={linkInfo.href} style={{ textDecoration: 'none' }} target={linkInfo.target} rel={linkInfo.rel}>
          {nameContent}
        </a>
      ) : nameContent}
      
      {properties.description_agence_fr && (
        <strong style={{ 
          fontSize: isMobile ? '12px' : '14px', 
          fontWeight: 400, 
          textAlign: 'left', 
          color: 'black',
          display: 'block',
          marginTop: '10px',
          ...multiLineEllipsisStyle
        }}>
          {properties.description_agence_fr}
        </strong>
      )}
    </div>
  );
  };

  const renderDateSection = () => {
    // Check if any date fields exist
    const hasDateInfo = properties.date_debut_expo || properties.date_fin_expo || 
                         properties.vernissage_date || properties.vernissage_hour;
    
    if (!hasDateInfo) return null;
    
    return (
      <div style={sectionStyle}>
        <h3 style={{ 
          fontSize: isMobile ? '14px' : '16px', 
          color: '#333', 
          marginTop: 0, 
          marginBottom: isMobile ? '8px' : '10px' 
        }}>
          Event Details
        </h3>
        
        {/* Exhibition dates */}
        {(properties.date_debut_expo || properties.date_fin_expo) && (
          <div style={{ marginBottom: isMobile ? '6px' : '8px' }}>
            <p style={{ margin: '0', fontSize: isMobile ? '12px' : '14px' }}>
              <strong>Date: </strong>
              {properties.date_debut_expo && formatDate(properties.date_debut_expo)}
              {properties.date_debut_expo && properties.date_fin_expo && 
               properties.date_debut_expo !== properties.date_fin_expo && ' - '}
              {properties.date_fin_expo && 
               properties.date_debut_expo !== properties.date_fin_expo && 
               formatDate(properties.date_fin_expo)}
            </p>
          </div>
        )}
        
        {/* Time/Hours info */}
        {properties.vernissage_hour && (
          <div style={{ marginBottom: isMobile ? '6px' : '8px' }}>
            <p style={{ margin: '0', fontSize: isMobile ? '12px' : '14px' }}>
              <strong>Time: </strong>
              {properties.vernissage_hour}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderDescription = () => (
    properties.description_projet && (
      <div style={sectionStyle}>
        {properties.titre_affiche && (
          <a 
            href={`https://connectedby.lefrenchdesign.org/project?pid=${properties.mongo_project_id}&cid=${properties.mongo_company_id}`}
            style={{ textDecoration: 'none' }}
          >
            <strong style={{ 
              color: '#0000ff', 
              fontSize: isMobile ? '16px' : '18px', 
              fontWeight: '500', 
              display: 'block', 
              textTransform: 'uppercase', 
              cursor: 'pointer'
            }}>
              <img 
                alt="" 
                style={{
                  margin: "0 5px 0 0", 
                  display: "inline-block", 
                  height: isMobile ? "12px" : "14px"
                }} 
                src="/img/fleche_bleue.png" 
              />
              {properties.titre_affiche} 
            </strong>
          </a>
        )}
        
        {properties.annee_projet && (
          <p style={{ 
            color: 'black', 
            fontSize: isMobile ? '12px' : '14px', 
            fontWeight: 'bold', 
            display: 'block', 
            textAlign: 'left', 
            marginTop: '10px', 
            marginBottom: '10px'
          }}>
            {properties.annee_projet}
          </p>
        )}
        
        <div style={{ position: 'relative' }}>
          <strong 
            ref={descriptionRef}
            style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              fontWeight: 400, 
              textAlign: 'left', 
              color: 'black', 
              lineHeight: '1.5',
              ...multiLineEllipsisStyle,
              maxHeight: descriptionExpanded ? '200px' : '120px', // Limit height when expanded to enable scrolling
            }}
          >
            {properties.description_projet}
          </strong>
          
          <button 
            onClick={() => setDescriptionExpanded(!descriptionExpanded)}
            style={{
              background: 'none',
              border: 'none',
              color: '#0039d6',
              cursor: 'pointer',
              padding: '5px 0',
              marginTop: '5px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: 600,
              display: isOverflowing ? 'block' : 'none',
              textAlign: 'left'
            }}
          >
            {descriptionExpanded ? 'Read Less' : 'Read More'}
          </button>
        </div>
      </div>
    )
  );

  const renderRSVPSection = () => {
    // Check if any RSVP information exists
    const hasRSVPInfo = properties.contact_rsvp || properties.rsvp;
    
    if (!hasRSVPInfo) return null;
    
    return (
      <div style={sectionStyle}>
        <h3 style={{ 
          fontSize: isMobile ? '14px' : '16px', 
          color: '#333', 
          marginTop: 0, 
          marginBottom: isMobile ? '8px' : '10px' 
        }}>
          RSVP Information
        </h3>
        
        {properties.rsvp && (
          <div>
            <p style={{ margin: '0', fontSize: isMobile ? '12px' : '14px' }}>
              <strong>RSVP: </strong>
              {properties.rsvp.startsWith('http') ? (
                <a 
                  href={properties.rsvp} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#0039d6', textDecoration: 'underline' }}
                >
                  Register Here
                </a>
              ) : properties.rsvp.includes('@') ? (
                <a 
                  href={`mailto:${properties.rsvp}`} 
                  style={{ color: '#0039d6', textDecoration: 'underline' }}
                >
                  {properties.rsvp}
                </a>
              ) : (
                properties.rsvp
              )}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderAllLinks = () => (
    (properties.site_web || properties.instagram || properties.email || 
     ((properties.projectType === 'oui' || properties.projectType === 'pdw') && properties.contact_rsvp) || properties.rsvp) && (
      <div style={{ 
        ...sectionStyle, 
        textAlign: 'center', 
        borderBottom: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '8px' : '10px',
        justifyContent: 'center',
        marginTop: '10px'
      }}>
        {properties.site_web && properties.site_web !== 'N/A' && (
          <a
            className="bluebutton"
            href={link(properties.site_web)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: isMobile ? '11px' : '12px',
              letterSpacing: '0.8px',
              padding: '6px 10px',
              backgroundColor: '#0039d6',
              border: '2px solid transparent',
              borderRadius: '4px',
              color: '#fff',
              cursor: 'pointer',
              textTransform: 'uppercase',
              transition: 'all .2s ease-in-out',
              textDecoration: 'none',
              width: 'calc(100% - 40px)',
              margin: 'auto',
              transform: 'scale(1)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.border = '2px solid #0039d6';
              e.currentTarget.style.color = '#0039d6';
              e.currentTarget.style.filter = 'drop-shadow(0px 0px 3px #5C81E6)';
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.querySelector('img').style.filter = 'invert(23%) sepia(97%) saturate(1894%) hue-rotate(214deg) brightness(97%) contrast(106%)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#0039d6';
              e.currentTarget.style.border = '2px solid transparent';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.filter = 'none';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.querySelector('img').style.filter = 'brightness(0) invert(1)';
            }}
          >
            <img 
              src="/img/weblogo.svg" 
              alt="Website"
              style={{ 
                width: isMobile ? '16px' : '20px', 
                height: isMobile ? '16px' : '20px',
                filter: 'brightness(0) invert(1)',
                marginRight: '10px'
              }}
            />
            <span style={{ 
              fontWeight: 600,
              fontSize: isMobile ? '11px' : '12px',
              letterSpacing: '0.8px',
              textTransform: 'uppercase'
            }}>
              Site Web
            </span>
          </a>
        )}
        
        {properties.instagram && (
          <a
            href={`https://instagram.com/${properties.instagram.replace('@', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bluebutton"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: isMobile ? '11px' : '12px',
              letterSpacing: '0.8px',
              padding: '6px 10px',
              backgroundColor: '#0039d6',
              border: '2px solid transparent',
              borderRadius: '4px',
              color: '#fff',
              cursor: 'pointer',
              textDecoration: 'none',
              width: 'calc(100% - 40px)',
              margin: 'auto',
              justifyContent: 'center',
              transition: 'all .2s ease-in-out',
              transform: 'scale(1)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.border = '2px solid #0039d6';
              e.currentTarget.style.color = '#0039d6';
              e.currentTarget.style.filter = 'drop-shadow(0px 0px 3px #5C81E6)';
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.querySelector('img').style.filter = 'invert(23%) sepia(97%) saturate(1894%) hue-rotate(214deg) brightness(97%) contrast(106%)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#0039d6';
              e.currentTarget.style.border = '2px solid transparent';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.filter = 'none';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.querySelector('img').style.filter = 'brightness(0) invert(1)';
            }}
          >
            <img 
              src="/img/logoinsta.svg" 
              alt="Instagram"
              style={{ 
                width: isMobile ? '16px' : '20px', 
                height: isMobile ? '16px' : '20px',
                filter: 'brightness(0) invert(1)'
              }}
            />
            <span style={{ 
              fontWeight: 600,
              fontSize: isMobile ? '11px' : '12px',
              letterSpacing: '0.8px',
              textTransform: 'uppercase'
            }}>
              Instagram
            </span>
          </a>
        )}
        
        {/* Email button - modified to use contact_rsvp if it's an Oui Design or Paris Design Week event */}
        {(properties.email || ((properties.projectType === 'oui' || properties.projectType === 'pdw') && properties.contact_rsvp)) && (
          <a
            href={`mailto:${(properties.projectType === 'oui' || properties.projectType === 'pdw') && properties.contact_rsvp ? properties.contact_rsvp : properties.email}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: isMobile ? '11px' : '12px',
              letterSpacing: '0.8px',
              padding: '6px 10px',
              backgroundColor: '#0039d6',
              border: '2px solid transparent',
              borderRadius: '4px',
              color: '#fff',
              cursor: 'pointer',
              textDecoration: 'none',
              width: 'calc(100% - 40px)',
              margin: 'auto',
              justifyContent: 'center',
              transition: 'all .2s ease-in-out',
              transform: 'scale(1)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.border = '2px solid #0039d6';
              e.currentTarget.style.color = '#0039d6';
              e.currentTarget.style.filter = 'drop-shadow(0px 0px 3px #5C81E6)';
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.querySelector('img').style.filter = 'invert(23%) sepia(97%) saturate(1894%) hue-rotate(214deg) brightness(97%) contrast(106%)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#0039d6';
              e.currentTarget.style.border = '2px solid transparent';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.filter = 'none';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.querySelector('img').style.filter = 'brightness(0) invert(1)';
            }}
          >
            <img 
              src="/img/email.svg" 
              alt="Email"
              style={{ 
                width: isMobile ? '16px' : '20px', 
                height: isMobile ? '16px' : '20px',
                filter: 'brightness(0) invert(1)'
              }}
            />
            <span style={{ 
              fontWeight: 600,
              fontSize: isMobile ? '11px' : '12px',
              letterSpacing: '0.8px',
              textTransform: 'uppercase'
            }}>
              {(properties.projectType === 'oui' || properties.projectType === 'pdw') && properties.contact_rsvp ? 'Email' : 'Email'}
            </span>
          </a>
        )}
        
        {/* RSVP Button */}
        {properties.rsvp && properties.rsvp.startsWith('http') && (
          <a
            href={properties.rsvp}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: isMobile ? '11px' : '12px',
              letterSpacing: '0.8px',
              padding: '6px 10px',
              backgroundColor: 'blue',
              border: '2px solid transparent',
              borderRadius: '4px',
              color: '#fff',
              cursor: 'pointer',
              textDecoration: 'none',
              width: 'calc(100% - 40px)',
              margin: 'auto',
              justifyContent: 'center',
              transition: 'all .2s ease-in-out',
              transform: 'scale(1)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.border = '2px solid #0039d6';
              e.currentTarget.style.color = '#0039d6';
              e.currentTarget.style.filter = 'drop-shadow(0px 0px 3px #5C81E6)';
              e.currentTarget.style.transform = 'scale(1.05)';
              // e.currentTarget.querySelector('img').style.filter = 'invert(23%) sepia(97%) saturate(1894%) hue-rotate(214deg) brightness(97%) contrast(106%)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#0039d6';
              e.currentTarget.style.border = '2px solid transparent';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.filter = 'none';
              e.currentTarget.style.transform = 'scale(1)';
              // e.currentTarget.querySelector('img').style.filter = 'brightness(0) invert(1)';
            }}
          >
            <span style={{ 
              fontWeight: 600,
              fontSize: isMobile ? '11px' : '12px',
              letterSpacing: '0.8px',
              textTransform: 'uppercase'
            }}>
              RSVP FOR THIS EVENT
            </span>
          </a>
        )}
      </div>
    )
  );

  // For touch devices, disable hover effects and increase button size
  useEffect(() => {
    if ('ontouchstart' in window || navigator.maxTouchPoints) {
      const style = document.createElement('style');
      style.innerHTML = `
        .bluebutton, a[href^="mailto"], a[href^="http"] {
          min-height: 44px !important; /* Ensure touch targets are at least 44px high */
        }
        
        /* Disable hover effects on touch devices */
        @media (hover: none) {
          .bluebutton:hover, a[href^="mailto"]:hover, a[href^="http"]:hover {
            transform: none !important;
            filter: none !important;
            background-color: inherit !important;
            color: inherit !important;
            border-color: inherit !important;
          }
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        document.head.removeChild(style);
      };
    }
  }, []);

  return (
    <div style={{ padding: '0', fontFamily:'SuisseIntl,sans-serif' }}>
      {renderCategories()}
      {renderMainInfo()}
      {renderDateSection()}
      {renderDescription()}
      {/* {renderRSVPSection()} */}
      <AddressComponent properties={properties} />
      {renderAllLinks()}
    </div>
  );
};

export default PropertiesContainer;